import { motion } from 'framer-motion';
import { Gift, Building2, Dumbbell, Trophy } from 'lucide-react';
import { GIFT_BOXES, ASSETS, WHATSAPP, waLink } from '../lib/products';
import { MessageCircle, Phone as PhoneIcon } from 'lucide-react';
import KrispButton from '../components/KrispButton';
import Marquee from '../components/Marquee';
import Footer from '../sections/Footer';

const OCCASIONS = [
  { icon: Dumbbell, title: 'GYM CREWS & CLUBS', desc: 'Post-session fuel for the whole squad.' },
  { icon: Building2, title: 'CORPORATE GIFTING', desc: 'Bulk boxes, custom sleeves, your logo on the crunch.' },
  { icon: Trophy, title: 'TOURNAMENTS & MATCH DAYS', desc: 'Sideline snacks that actually pull their weight.' },
  { icon: Gift, title: 'JUST BECAUSE', desc: 'Someone trains hard. Reward them properly.' },
];

export default function GiftingPage() {
  const GIFT_WA = waLink('Hi, interested in gifting');
  return (
    <main data-testid="gifting-page" className="bg-cream min-h-screen">
      <section className="relative overflow-hidden bg-maroon text-cream">
        <div className="absolute inset-0 opacity-[0.08] bg-[repeating-linear-gradient(135deg,transparent_0_28px,#F7B500_28px_30px)]" />
        <div className="absolute -top-20 -right-20 w-[40vw] h-[40vw] rounded-full bg-kred/50" />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-14 py-16 md:py-24 grid md:grid-cols-2 items-center gap-10">
          <div>
            <p className="font-cond text-xl tracking-[0.3em] text-kyellow">FUEL AS A GIFT</p>
            <h1 className="font-display text-[18vw] md:text-[8vw] leading-[0.85] mt-2">GIFT THE<br />GRIND.</h1>
            <p className="text-base text-cream/80 mt-5 max-w-md">Gold-foiled black gift boxes — a four-pack box or the Power Box with a PROKRISP shaker — for gym crews, offices, festivals and the people who train hard and snack smart. Message us on WhatsApp to order.</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <KrispButton testid="gifting-explore" onClick={() => document.getElementById('boxes')?.scrollIntoView({ behavior: 'smooth' })}>EXPLORE GIFTING OPTIONS ↓</KrispButton>
              <a href={GIFT_WA} target="_blank" rel="noreferrer" data-testid="gifting-contact"><KrispButton variant="ghost"><MessageCircle size={16} /> CONTACT US NOW</KrispButton></a>
            </div>
            <a href={GIFT_WA} target="_blank" rel="noreferrer" data-testid="gifting-number" className="inline-flex items-center gap-2 mt-5 font-cond font-bold tracking-wider text-kyellow hover:text-cream transition-colors">
              <PhoneIcon size={16} /> {WHATSAPP.display} · WHATSAPP US
            </a>
          </div>
          <div className="relative h-72 md:h-[460px] flex items-center justify-center">
            <img src={ASSETS.box50} alt="PROKRISP 50g gift box" loading="lazy" className="absolute h-[70%] object-contain gpu left-0 -rotate-6 chip-drift" style={{ '--r': '-6deg', '--t': '8s' }} />
            <img src={ASSETS.box100} alt="PROKRISP 100g power box" loading="lazy" className="absolute h-[78%] object-contain gpu right-0 rotate-3 chip-drift z-10" style={{ '--r': '3deg', '--t': '9s' }} />
          </div>
        </div>
      </section>
      <Marquee text="GIFT BUNDLES · CORPORATE CRUNCH · TEAM BOXES · MIX. MATCH. MUNCH. ·" />

      <section id="boxes" className="max-w-[1400px] mx-auto px-5 md:px-14 py-16 scroll-mt-24">
        <h2 className="font-display text-6xl md:text-8xl text-cocoa leading-none">THE BOXES</h2>
        <p className="font-cond text-sm tracking-[0.3em] text-cocoa/60 mt-2">GO BEYOND THE ORDINARY. PICK A BOX, MESSAGE US ON WHATSAPP.</p>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          {GIFT_BOXES.map((b, i) => (
            <motion.div key={b.id} data-testid={`gift-box-${b.id}`} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="card-pop rounded-[2rem] border-2 border-cocoa bg-white/70 overflow-hidden flex flex-col">
              <div className="h-72 md:h-80 flex items-center justify-center relative overflow-hidden p-6" style={{ background: `${b.color}22` }}>
                <span className="absolute top-4 left-4 z-10 rounded-full px-3 py-1 text-[10px] font-black tracking-[0.2em] text-cream bg-cocoa">{b.items}</span>
                <img src={b.img} alt={b.name} loading="lazy" className="h-full object-contain gpu" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-display text-4xl text-cocoa leading-none">{b.name}</h3>
                <p className="text-sm text-cocoa/70 mt-3 flex-1">{b.desc}</p>
                <div className="flex items-end justify-between mt-5">
                  <p className="font-display text-4xl leading-none" data-testid={`gift-price-${b.id}`}>₹{b.price}</p>
                  <span className="rounded-full bg-cocoa text-kyellow px-3 py-1 text-[10px] font-black tracking-widest">{b.items}</span>
                </div>
                <a href={waLink(`Hi, interested in gifting — ${b.name} (${b.items})`)} target="_blank" rel="noreferrer" className="block mt-5"><KrispButton testid={`gift-contact-${b.id}`} color={b.color} className="w-full"><MessageCircle size={16} /> CONTACT US NOW</KrispButton></a>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="combos" className="bg-kyellow rounded-[2.5rem] mx-3 md:mx-8 px-6 md:px-14 py-14 scroll-mt-24">
        <div className="max-w-[1300px] mx-auto grid md:grid-cols-[1fr_2fr] gap-10 items-center">
          <div>
            <h2 className="font-display text-6xl md:text-7xl text-cocoa leading-[0.9]">EVERY<br />OCCASION.</h2>
            <p className="text-sm text-cocoa/70 mt-4">Custom sleeves, handwritten notes and bulk pricing available for orders of 25+ boxes.</p>
            <a href={GIFT_WA} target="_blank" rel="noreferrer" className="inline-block mt-6"><KrispButton testid="combos-enquire" variant="dark"><MessageCircle size={16} /> WHATSAPP {WHATSAPP.display} →</KrispButton></a>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {OCCASIONS.map(({ icon: Icon, title, desc }) => (
              <div key={title} data-testid={`occasion-${title.toLowerCase().split(' ')[0]}`} className="rounded-2xl bg-cream border-2 border-cocoa p-5 card-pop">
                <span className="w-11 h-11 rounded-full bg-cocoa text-kyellow flex items-center justify-center"><Icon size={20} /></span>
                <p className="font-cond font-bold text-lg text-cocoa mt-3">{title}</p>
                <p className="text-sm text-cocoa/70 mt-1">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mt-16"><Footer /></div>
    </main>
  );
}
